import React, { Fragment, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IStore } from '../../store';
import DynamicTextRenderer from '../openAIChat/ChatReply/ChatSteamResponseFormatting';
import styled from 'styled-components';
import {
  setActivityPanelActiveTab,
  setActivityPanelTitle,
  setFocusedMessage,
  setShowActivityPanel
} from '../../store/actions/deepResearchActions';
import { deepResearchDownload, deepResearchRegenerateIcon } from '../../commonImage';
import {
  DOWNLOAD_FILE_TYPES,
  downloadBlob,
  getCopyStatusIcon,
  getFaviconUrl,
  handleFaviconError,
  removeQuotes
} from './Utils';
import ResponseTools from './ResponseTools';
import { downloadDeepResearchChat } from '../../services/deepResearch.service';
import completeIcon from '../../assets/images/deep-research/complete.svg';
import warningIcon from '../../assets/images/deep-research/icon_warning.svg';
import LoadingIndicator from '../openAIChat/LoadingIndicator';

export interface ThreadMessage {
  messageId?: string;
  type: string; // Either clarify or procced
  content: string;
  instructions?: '';
  activityPanelTitle?: string;
  activities?: [];
  citations?: [];
  sources?: [];
  researchTime?: '';
  search_type?: string;
}

const getResponseContent = (response: string, attribute: string) => {
  try {
    const parsed = JSON.parse(response);
    // Content could be string, array, or null
    if (Array.isArray(parsed[attribute])) {
      return parsed[attribute].join('\n');
    }
    if (typeof parsed[attribute] === 'string') {
      return parsed[attribute];
    }
    return '';
  } catch {
    return '';
  }
};

const getResponseType = (response: string) => {
  try {
    const parsed = JSON.parse(response);
    if (typeof parsed.type === 'string') {
      return parsed.type;
    }
    return '';
  } catch {
    return '';
  }
};

interface ThreadViewProps {
  onRegenerate?: (id: string, message: ThreadMessage) => void;
}

const ThreadView: React.FC<ThreadViewProps> = ({ onRegenerate }) => {
  const dispatch = useDispatch();
  const selectedChatThread = useSelector((store: IStore) => store.userChat.selectedChatThread);
  const userQuery = useSelector((state: IStore) => state.deepResearch.userQuery);
  const selectedThreadMessages = useSelector(
    (state: IStore) => state.deepResearch.selectedThreadMessages
  ) as ThreadMessage[];

  const focusedMessage = useSelector((state: any) => state.deepResearch.focusedMessage);
  const currentMessageActivityTitle = useSelector(
    (state: any) => state.deepResearch?.activityPanelTitle
  );
  const activityTitleRef = useRef(currentMessageActivityTitle);
  const [currentFocusedMessage, setCurrentFocusedMessage] = useState<any>(null);
  const [focusedMessageResponse, setFocusedMessageResponse] = useState<any>(null);

  useEffect(() => {
    activityTitleRef.current = currentMessageActivityTitle;
  }, [currentMessageActivityTitle]);

  useEffect(() => {
    let formattedResponse: any;
    try {
      formattedResponse = JSON.parse(focusedMessage?.response);
    } catch (e) {}
    if (
      formattedResponse?.activityPanelTitle &&
      formattedResponse?.activityPanelTitle?.length > 0
    ) {
      dispatch(setActivityPanelTitle(removeQuotes(formattedResponse?.activityPanelTitle)));
    }
    setFocusedMessageResponse(formattedResponse);
    setCurrentFocusedMessage(focusedMessage);
  }, [focusedMessage]);

  const openActivityPanel = (currentThreadMessage?: any) => {
    dispatch(setShowActivityPanel(true));
    if (
      currentThreadMessage &&
      (!focusedMessage ||
        focusedMessage.messageId !== currentThreadMessage.messageId ||
        focusedMessage.tempId !== currentThreadMessage.tempId)
    ) {
      dispatch(setFocusedMessage(currentThreadMessage));
    }
  };

  const handleSidebarTabClick = (tab: string, currentThreadMessage?: any) => {
    dispatch(setActivityPanelActiveTab(tab));
    dispatch(setShowActivityPanel(true));
    if (
      currentThreadMessage &&
      (!focusedMessage ||
        focusedMessage.messageId !== currentThreadMessage.messageId ||
        focusedMessage.tempId !== currentThreadMessage.tempId)
    ) {
      dispatch(setFocusedMessage(currentThreadMessage));
    }
  };

  const renderResearchCompletionInfo = (
    target: any,
    threadMessage?: any,
    isFromThreadView?: boolean
  ) => {
    let responseObj: any = target;
    if (responseObj && typeof responseObj.response === 'string') {
      try {
        responseObj = JSON.parse(responseObj.response);
      } catch {
        responseObj = {};
      }
    }

    return (
      <p
        className={`deep-research-status-text ${isFromThreadView ? 'thread-view-status-text' : ''}`}
        onClick={() => openActivityPanel(threadMessage)}>
        Research completed in {responseObj?.researchTime} ·{' '}
        <span onClick={() => handleSidebarTabClick('sources', threadMessage)}>
          {responseObj?.sources?.length} sources
        </span>{' '}
        ·{' '}
        <span onClick={() => handleSidebarTabClick('activity', threadMessage)}>
          {responseObj?.activities?.length} searches
        </span>
      </p>
    );
  };

  const [copyStatus, setCopyStatus] = useState<Record<string, number>>({});
  const handleCopyChat = (messageId: string, targetResponse: string) => {
    if (targetResponse?.length > 0) {
      navigator.clipboard.writeText(targetResponse);
      setCopyStatus((prev) => ({ ...prev, [messageId]: 1 }));
    } else {
      setCopyStatus((prev) => ({ ...prev, [messageId]: 2 }));
    }
    setTimeout(() => {
      setCopyStatus((prev) => ({ ...prev, [messageId]: 0 }));
    }, 1000);
  };

  const [toastDownload, setToastDownload] = useState<any>({});
  const [isToast, setIsToast] = useState(false);
  const [isChatReportDownloading, setIsChatReportDownloading] = useState(false);
  const handleDownloadChatAsWord = async (selectedMessageId: string) => {
    let selectedMessage: any = selectedThreadMessages.find(
      (m) => m.messageId === selectedMessageId
    );
    let messageType = getResponseType(selectedMessage?.response);
    if (messageType === 'final') dispatch(setFocusedMessage(selectedMessage));
    let downloadBlobTitle = getDownloadFileName(selectedMessage);
    let downloadPayload = {
      threadId: selectedChatThread?.threadId,
      messageId: selectedMessageId
    };
    const downloadReportResponse: any = await downloadDeepResearchChat(downloadPayload, 'blob');
    if (
      downloadReportResponse &&
      downloadReportResponse?.size > 0 &&
      downloadReportResponse?.type === DOWNLOAD_FILE_TYPES.DOCX
    ) {
      downloadBlob(downloadReportResponse, `${downloadBlobTitle}.docx`, DOWNLOAD_FILE_TYPES.DOCX);
      const toast = {
        message: 'Chat report generated successfully.',
        type: 'success'
      };
      setToastDownload(toast);
      setIsToast(true);
      setTimeout(() => {
        setIsToast(false);
      }, 10000);
      setIsChatReportDownloading(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      const toast = {
        message: 'Chat report generation failed. Please try again.',
        type: 'error'
      };
      setToastDownload(toast);
      setIsToast(true);
      setTimeout(() => {
        setIsToast(false);
      }, 10000);
      setIsChatReportDownloading(false);
    }
  };

  const getDownloadFileName = (message: any): string => {
    const DEFAULT_FILENAME = 'CompetitiveEdge_DeepResearch';

    try {
      const parsed = JSON.parse(message.response || '{}');
      return parsed.type === 'final'
        ? parsed.activityPanelTitle || DEFAULT_FILENAME
        : message.question || DEFAULT_FILENAME;
    } catch {
      return message.question || DEFAULT_FILENAME;
    }
  };

  const closeIcon = () => {
    return (
      <svg
        width='14'
        height='14'
        viewBox='0 0 14 14'
        fill='none'
        xmlns='http://www.w3.org/2000/svg'>
        <path
          d='M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z'
          fill='white'
        />
      </svg>
    );
  };

  const renderSourcesDownloadContainer = (researchInfoObj: any, threadMessage?: any) => {
    if (!(researchInfoObj?.sources?.length > 0)) return null;

    const domainMap = new Map<string, string>();
    for (const c of researchInfoObj.sources) {
      if (c.url) {
        const domain = new URL(c.url).hostname.replace('www.', '');
        if (!domainMap.has(domain)) {
          domainMap.set(domain, c.url);
        }
      }
    }

    return (
      <div className='deep-research-source-download-container'>
        <button
          className='deep-research-citation-pill'
          onClick={() => handleSidebarTabClick('sources', threadMessage)}>
          <div className='deep-research-citation-pill-icons'>
            {Array.from(domainMap.entries()).map(([domain, url], i) => (
              <img
                key={i}
                src={getFaviconUrl({ url, size: '24' })}
                alt={domain}
                className='deep-research-citation-favicon-img'
                onError={handleFaviconError}
              />
            ))}
          </div>
          <span className='deep-research-citation-pill-label'>Sources</span>
        </button>
      </div>
    );
  };

  const isFinalResponseContentExists = (target: any) => {
    let responseObj: any = target;
    if (responseObj && typeof responseObj?.response === 'string') {
      try {
        responseObj = JSON.parse(responseObj?.response);
      } catch {
        responseObj = {};
      }
    }
    return (
      responseObj &&
      responseObj.type === 'final' &&
      typeof responseObj.content === 'string' &&
      responseObj?.content?.length > 0
    );
  };

  return (
    <Container>
      {selectedThreadMessages.map((currentThreadMessage: any, idx: number) => {
        const id = currentThreadMessage.messageId || String(idx);
        const responseContent = getResponseContent(currentThreadMessage.response, 'content');
        let responseObj: any = {};
        try {
          responseObj = JSON.parse(currentThreadMessage.response);
        } catch {}
        const isFocused =
          currentFocusedMessage &&
          ((currentFocusedMessage.messageId &&
            currentFocusedMessage.messageId === currentThreadMessage.messageId) ||
            (currentFocusedMessage.tempId &&
              currentFocusedMessage.tempId === currentThreadMessage.tempId));
        const researchInfoObj = isFocused
          ? {
              ...currentFocusedMessage,
              // Always use the latest arrays from responseObj if present
              activities: responseObj.activities ?? currentFocusedMessage.activities,
              sources: responseObj.sources ?? currentFocusedMessage.sources,
              citations: responseObj.citations ?? currentFocusedMessage.citations,
              researchTime: responseObj.researchTime ?? currentFocusedMessage.researchTime
            }
          : responseObj;

        return (
          <Fragment key={id}>
            <StyledUserSection aria-label='User query'>
              {currentThreadMessage.question || userQuery}
            </StyledUserSection>
            <div className='deep-research-chat-tools question-section-tools'>
              <span
                className='deep-research-chat-tools-icon'
                title={
                  copyStatus[`${id}-question`] === 0 || copyStatus[`${id}-question`] === undefined
                    ? 'Copy to clipboard'
                    : copyStatus[`${id}-question`] === 1
                      ? 'Copied!'
                      : 'Copy failed'
                }
                onClick={() =>
                  handleCopyChat(`${id}-question`, currentThreadMessage.question || userQuery)
                }>
                {getCopyStatusIcon(copyStatus[`${id}-question`] || 0)}
              </span>
            </div>
            {currentThreadMessage?.isLoading ? (
              <div className='loading-div'>
                <LoadingIndicator />
              </div>
            ) : (
              currentThreadMessage.response && (
                <>
                  <StyledResponseSection
                    aria-label='Assistant response'
                    className='instructions-response-container'>
                    <DynamicTextRenderer
                      text={getResponseContent(currentThreadMessage.response, 'instructions')}
                    />
                  </StyledResponseSection>

                  <StyledResponseSection
                    className={
                      getResponseType(currentThreadMessage.response) === 'final'
                        ? 'final-response-container'
                        : ''
                    }
                    aria-label='Assistant response'
                    isResearchContent={getResponseType(currentThreadMessage.response) === 'final'}>
                    {isFinalResponseContentExists(researchInfoObj) &&
                      renderResearchCompletionInfo(researchInfoObj, currentThreadMessage, true)}
                    <div
                      className={
                        isFinalResponseContentExists(researchInfoObj)
                          ? 'deep-research-chat-container'
                          : 'deep-research-chat-clarify-container'
                      }>
                      <DynamicTextRenderer text={responseContent} />
                      {isFinalResponseContentExists(researchInfoObj) &&
                        renderSourcesDownloadContainer(researchInfoObj, currentThreadMessage)}
                    </div>
                    {getResponseType(currentThreadMessage.response) !== 'final' && (
                      <ResponseTools
                        id={id}
                        responseType={getResponseType(currentThreadMessage.response)}
                        responseContent={responseContent}
                        copyStatus={copyStatus}
                        handleCopyChat={handleCopyChat}
                        handleDownloadChatAsWord={() => handleDownloadChatAsWord(id)}
                        deepResearchDownload={deepResearchDownload}
                        deepResearchRegenerateIcon={deepResearchRegenerateIcon}
                        onRegenerate={() => onRegenerate && onRegenerate(id, currentThreadMessage)}
                        isFinalReady={isFinalResponseContentExists(researchInfoObj)}
                        isLastMessage={idx === selectedThreadMessages.length - 1}
                      />
                    )}
                    {getResponseType(currentThreadMessage.response) === 'final' &&
                      isFinalResponseContentExists(researchInfoObj) && (
                        <ResponseTools
                          id={id}
                          responseType={getResponseType(currentThreadMessage.response)}
                          responseContent={responseContent}
                          copyStatus={copyStatus}
                          handleCopyChat={handleCopyChat}
                          handleDownloadChatAsWord={() => handleDownloadChatAsWord(id)}
                          deepResearchDownload={deepResearchDownload}
                          deepResearchRegenerateIcon={deepResearchRegenerateIcon}
                          onRegenerate={() =>
                            onRegenerate && onRegenerate(id, currentThreadMessage)
                          }
                          isFinalReady={isFinalResponseContentExists(researchInfoObj)}
                          isLastMessage={idx === selectedThreadMessages.length - 1}
                        />
                      )}
                  </StyledResponseSection>
                </>
              )
            )}
            {isToast && (
              <StyledToast>
                <div className={`toast ${toastDownload.type}`}>
                  <div className='toast-message'>
                    <div
                      className={
                        toastDownload.type === 'error'
                          ? 'warning-icon img-s'
                          : 'complete-icon img-s'
                      }></div>
                    {toastDownload.message}
                  </div>
                  <span className='pointer' onClick={() => setIsToast(false)}>
                    {closeIcon()}
                  </span>
                </div>
              </StyledToast>
            )}
          </Fragment>
        );
      })}
    </Container>
  );
};

const Container = styled.div`
  .deep-research-chat-clarify-container ol,
  p,
  strong {
    font-family: 'ey_interstate';
    font-style: normal;
    font-weight: 300;
    margin-top: 0;
  }
  .deep-research-chat-tools {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 14px;
    margin-top: 15px;
    margin-inline-start: 0;
    margin: auto;
    padding-left: 5px;
    padding-top: 15px;
    margin-bottom: 2rem;
  }

  .deep-research-chat-tools-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
  }

  .regenerate-icon {
    max-height: 16px;
  }
  .question-section-tools {
    margin: 0;
    padding: 0px;
    width: 100%;
    justify-content: flex-end;
  }
  .answer-section-tools {
    margin: 0;
    margin-bottom: 1rem;
    padding: 0px;
  }
`;

const StyledUserSection = styled.div`
  display: flex;
  padding: 12px 16px;
  align-items: center;
  gap: 6.35px;
  border-radius: 8px 8px 0 8px;
  background: #f3f3f5;
  backdrop-filter: blur(30px);
  color: #1a1a24;
  font-feature-settings:
    'liga' off,
    'clig' off;
  font-family: 'ey_interstate';
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 16px; /* 114.286% */
  max-width: 55%;
  margin-left: auto;
  margin-right: 0;
  margin-bottom: 5px;
`;

const StyledResponseSection = styled.div<{ isResearchContent?: boolean }>`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  color: #1a1a24;
  font-family: EYInterstate;
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 20px;
  width: ${(props) => (props.isResearchContent ? '99%' : '65%')};
  margin-right: auto;
  margin-left: 0;
  background: #fff;
  border-radius: 8px 8px 8px 0;

  .deep-research-chat-container {
    width: 100%;
    // margin: ${(props) => (props.isResearchContent ? '0px 0px 2rem' : 'auto')};
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    border: 1px solid #dedee2;
    background: #f9f9fa;
    border-radius: 8px;
    overflow-x: hidden;
    color: #4e4e5d;
    font-family: 'ey_interstate';
    font-size: 14px;
    font-style: normal;
    font-weight: 300;
    padding: 1rem;
    word-break: break-word;
    line-height: normal;
  }

  .deep-research-chat-container h1 {
    color: #1a1a24;
    font-family: 'ey_interstate';
    font-size: 20px;
    font-style: normal;
    font-weight: 400;
    line-height: 30.002px; /* 150.01% */
  }

  .deep-research-chat-container strong {
    font-family: 'ey_interstate';
    font-style: normal;
    font-weight: 300;
  }

  .deep-research-chat-container h2,
  h3,
  h4 {
    color: #1a1a24;
    font-family: 'ey_interstate';
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 23.996px; /* 149.956% */
  }
  .deep-research-chat-container .markdown-link {
    border-radius: 20px;
    background: #cce9ff;
    padding: 0px 6px;
    text-decoration: none;
    color: #0a558e;
    font-size: 10px;
    font-weight: 500;
    height: 16px;
    padding: 0px 6px;
    padding-bottom: 2px;
  }
  /* Status Text */
  .deep-research-status-text {
    color: #7f7f91;
    font-family: 'EYInterstate';
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    cursor: pointer;
  }
  .final-response-container {
    margin: 0 2.5rem;
    gap: 16px;
  }
  .final-answer-section-tools {
    margin: 1rem 0;
    padding: 0px;
  }
  .instructions-response-container p {
    margin-top: 1rem;
  }
`;

const StyledToast = styled.div`
  .toast {
    background: #2e2e3c;
    position: fixed;
    top: 75px;
    right: 15px;
    z-index: 9999999;
    display: flex;
    padding: 2rem 1rem;
    width: 400px;
    height: 44px;
    justify-content: space-between;
    align-items: center;
  }

  .toast-message {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .img-s {
    width: 24px;
    height: 24px;
  }

  .img-m {
    width: 30px;
    height: 30px;
  }

  .warning-icon {
    background-image: url(${warningIcon});
    background-repeat: no-repeat;
    background-size: contain;
  }

  .complete-icon {
    background-image: url(${completeIcon});
    background-repeat: no-repeat;
    background-size: contain;
  }
`;
export default ThreadView;
